import styled from '@emotion/styled';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useAuthState } from '../../../../lib/context/auth.context';
import { useIsScrolling } from '../../../../lib/hooks/useIsScrolling';
import type { PropertyData, PublicBookingWithAuctionBid } from '../../../../lib/models';
import { trpc } from '../../../../lib/trpc';
import AuctionBidContent from './AuctionBidContent';
import BookingContent from './BookingContent';
import NewBookingContent from './NewBookingContent';

interface PropertyFloatingCardProps {
  property: PropertyData;
}

const PropertyFloatingCard: React.FC<React.PropsWithChildren<PropertyFloatingCardProps>> = ({
  property,
}) => {
  const { status: authStatus } = useAuthState();

  const [canShowFloatingBar, setCanShowFloatingBar] = useState(false);
  const isScrolling = useIsScrolling().scrollDirectionY === 'down';

  useEffect(() => {
    if (!canShowFloatingBar) {
      setTimeout(() => setCanShowFloatingBar(true), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: bookingData, isLoading: isBookingLoading } = trpc.bookings.findByProperty.useQuery(
    { propertyId: property.id },
    { enabled: authStatus === 'loggedIn' },
  );
  const booking = bookingData?.booking ?? null;

  const isLoading = authStatus === 'loading' || isBookingLoading;
  const floatingBarVisible = canShowFloatingBar && !isScrolling;

  return (
    <>
      <div
        className={clsx(
          'z-20 fixed bottom-0 inset-x-0 max-w-[100vw] transition-transform duration-300 delay-100',
          'lg:static lg:min-w-[24rem] lg:ml-6',
          isLoading
            ? 'translate-y-full lg:translate-y-0'
            : floatingBarVisible
            ? 'translate-y-0'
            : 'translate-y-[calc(100%-56px)] lg:translate-y-0',
        )}
      >
        <CardWrapper
          className={clsx(
            'w-full border-t bg-white',
            'lg:sticky lg:top-24 lg:p-0 lg:border-t-0 lg:bg-transparent',
          )}
        >
          <div
            className={clsx(
              'flex w-full h-full bg-white',
              'lg:flex-col lg:shadow-lg lg:border lg:rounded-md',
            )}
          >
            {isLoading ? (
              <div className="hidden lg:flex flex-col w-full p-4">
                <div className="skeleton h-8 w-20" />
                <div className="py-3.5">
                  <hr />
                </div>
                <div className="space-y-2">
                  <div className="skeleton h-8 w-[90%]" />
                  <div className="skeleton h-8 w-full" />
                </div>
              </div>
            ) : (
              <ContentSwitch property={property} booking={booking} />
            )}
          </div>
        </CardWrapper>
      </div>
    </>
  );
};

const CardWrapper = styled('div')`
  @media (max-width: 1024px) {
    box-shadow: 0 0 14px -3px rgba(0, 0, 0, 0.08);
  }
`;

interface ContentSwitchProps {
  booking: PublicBookingWithAuctionBid | null;
  property: PropertyData;
}

const ContentSwitch: React.FC<React.PropsWithChildren<ContentSwitchProps>> = ({
  booking,
  property,
}) => {
  if (!booking) {
    return <NewBookingContent property={property} />;
  }

  if (booking.status === 'PENDING' && booking.auctionBid !== null) {
    return (
      <AuctionBidContent
        // this spread operator is just to "remember" typescript booking.auctionBid is not null
        booking={{ ...booking, auctionBid: booking.auctionBid }}
      />
    );
  }

  return <BookingContent booking={booking} />;
};

export default PropertyFloatingCard;
